import { Component, Input, OnInit, inject } from '@angular/core';
import { Breadcrumb } from '@app/shared/utils/route-utils';
import * as fromRoot from '@app/store/reducers';
import { Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import { map, withLatestFrom } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TruncateWithEllipsesPipe } from '@app/shared/pipes/truncate-with-ellipses.pipe';
import { MatIcon } from '@angular/material/icon';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatAnchor } from '@angular/material/button';
import { AsyncPipe } from '@angular/common';
import { withUrlSegmentParams } from './breadcrumb-link';

@UntilDestroy()
@Component({
    selector: 'app-breadcrumbs',
    templateUrl: './breadcrumbs.component.html',
    styleUrls: ['./breadcrumbs.component.scss'],
    imports: [
        MatAnchor,
        RouterLink,
        MatIcon,
        AsyncPipe,
        TruncateWithEllipsesPipe,
    ]
})
export class BreadcrumbsComponent implements OnInit {
  private store = inject(Store);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  @Input()
  maxSegments: number;

  @Input()
  maxLabelSegmentLength: number;

  breadcrumbs$: Observable<Breadcrumb[]>;

  ngOnInit() {
    this.breadcrumbs$ = this.store.select(fromRoot.selectRouterRouteData).pipe(
      untilDestroyed(this),
      map((routeData) => {
        const breadcrumbs: Breadcrumb[] = [];

        routeData
          .filter(rData => !!rData.breadcrumbs)
          .map(rData => rData.breadcrumbs)
          .forEach((bcrumbs) => {
            breadcrumbs.push(...bcrumbs);
          });

        return breadcrumbs;
      }),
      withLatestFrom(this.store.select((fromRoot.selectRouterFlatParams))),

      // Substitute actual route parameters
      map(([breadcrumbs, flatParams]) => {
        return breadcrumbs.map(breadcrumb => ({
          ...breadcrumb,
          path: breadcrumb.path.map((pathSegment) => {
            if (/:[a-z][a-zA-Z0-9]+/.test(pathSegment)) {
              return flatParams[pathSegment.slice(1)];
            }

            return pathSegment;
          }),
        }));
      }),

      // Populate labels from the store
      map((breadcrumbs) => {
        return breadcrumbs.map(breadcrumb => ({
          ...breadcrumb,
          label$: breadcrumb.labelSelector
            ? this.store.select(breadcrumb.labelSelector).pipe(

                map(label => `${label}`),
                untilDestroyed(this))
            : null,
        }));
      }),
    );

    // Crumb paths are relative to this component's route, e.g., `app` for LayoutComponent
    const urlSegments$ = this.store.select(fromRoot.selectRouterUrl).pipe(
      map(url => (this.router.parseUrl(url).root.children.primary?.segments ?? [])
        .slice(this.route.snapshot.pathFromRoot.flatMap(route => route.url).length)),
    );

    // Populate the router links, keeping the state, e.g., the page, of the lists they pass through
    this.breadcrumbs$ = combineLatest([this.breadcrumbs$, urlSegments$]).pipe(
      untilDestroyed(this),
      map(([breadcrumbs, urlSegments]) => {
        const currentLink = [];

        return breadcrumbs.map((breadcrumb) => {
          currentLink.push(...breadcrumb.path);
          return {
            ...breadcrumb,
            routerLink: withUrlSegmentParams(currentLink, urlSegments),
          };
        });
      }),
    );
  }
}
